// =============================================================================
// features/meu-whatsapp/connectionGuard.ts — Regra pura da guarda de conexão
// do WhatsApp do vendedor (F57-S02). Plano: docs/planejamento-guarda-conexao-whatsapp.md.
//
// Quem decide QUEM é obrigado e SE está desconectado é a API
// (`GET /api/me/whatsapp-connection` → `required` + `needs_action`). Esta
// camada só decide QUANDO mostrar, sem React e sem rede, para ser provada em
// teste (o projeto não renderiza React nos testes).
//
// Dedupe do modal: por ABA (`sessionStorage`) e por QUEDA. A chave inclui o
// `since` da desconexão: "Depois" dispensa ESTA queda nesta aba; uma nova queda
// (novo `since`) ou uma nova aba trazem o modal de volta. O selo na topbar não
// é dispensável: fica enquanto durar a desconexão.
// =============================================================================

import { isConnectPromptRouteExcluded } from './connectPrompt';

/** Flag das 4 camadas (nasce desligada — migration 0163). */
export const CONNECTION_GUARD_FLAG = 'whatsapp.connection_guard.enabled';

export const CONNECTION_STATUSES = [
  'WORKING',
  'STARTING',
  'SCAN_QR_CODE',
  'FAILED',
  'STOPPED',
  'NO_CHANNEL',
  'UNKNOWN',
] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const CONNECTION_REASONS = [
  'failed',
  'disconnected',
  'awaiting_qr',
  'starting',
  'stuck_starting',
  'never_connected',
  'unknown',
] as const;
export type ConnectionReason = (typeof CONNECTION_REASONS)[number];

export interface WhatsAppConnection {
  readonly required: boolean;
  readonly agent_id: string | null;
  readonly channel_id: string | null;
  readonly status: ConnectionStatus;
  readonly reason: ConnectionReason | null;
  readonly since: string | null;
  readonly checked_at: string | null;
  readonly needs_action: boolean;
}

/** Motivo em português simples, para quem está no meio de um atendimento. */
export const REASON_LABEL: Record<ConnectionReason, string> = {
  failed: 'A conexão falhou',
  disconnected: 'O celular foi desconectado',
  awaiting_qr: 'Falta ler o código com o celular',
  starting: 'Conectando',
  stuck_starting: 'A conexão travou ao iniciar',
  never_connected: 'Seu WhatsApp ainda não foi conectado a este sistema',
  unknown: 'Sem informação recente',
};

/** Passo a passo no celular (Android e iPhone). */
export const CONNECT_STEPS: readonly string[] = [
  'Toque em "Conectar agora" — o código aparece aqui na tela.',
  'No celular, abra o WhatsApp e toque em ⋮ (Android) ou em Configurações (iPhone).',
  'Entre em "Aparelhos conectados" e toque em "Conectar um aparelho".',
  'Aponte a câmera do celular para o código.',
];

// ---------------------------------------------------------------------------
// Decisões
// ---------------------------------------------------------------------------

export interface GuardModalInput {
  readonly flagEnabled: boolean;
  /** Resposta da API; `null` enquanto carrega ou quando falhou (fail-closed). */
  readonly connection: WhatsAppConnection | null;
  readonly pathname: string;
  /** Esta queda já foi dispensada nesta aba. */
  readonly dismissed: boolean;
  /** Modal obrigatório de e-mail pessoal ativo/carregando: este espera. */
  readonly personalEmailPending: boolean;
}

/**
 * Abre o modal? Todas as condições (AND). `needs_action` já embute "obrigado"
 * e "desconectado" — e nunca vale para `UNKNOWN` (servidor nosso fora do ar
 * não é culpa do vendedor). Checar `required` de novo é defesa em
 * profundidade: quem não é obrigado (ex.: gestora com papel de agente) nunca
 * vê nada, mesmo se a API errar o `needs_action`.
 */
export function shouldOpenGuardModal(input: GuardModalInput): boolean {
  const { flagEnabled, connection, pathname, dismissed, personalEmailPending } = input;
  if (!flagEnabled || connection === null) return false;
  if (!connection.required || !connection.needs_action) return false;
  if (isConnectPromptRouteExcluded(pathname)) return false;
  if (dismissed) return false;
  if (personalEmailPending) return false;
  return true;
}

/** Selo persistente na topbar: enquanto durar a desconexão, sem dispensa. */
export function shouldShowStatusPill(
  flagEnabled: boolean,
  connection: WhatsAppConnection | null,
  pathname: string,
): boolean {
  if (!flagEnabled || connection === null) return false;
  if (!connection.required || !connection.needs_action) return false;
  // Na tela "Meu WhatsApp" o próprio conteúdo já é o caminho para conectar.
  return !isConnectPromptRouteExcluded(pathname);
}

/**
 * Voltou a conectar? (para a confirmação quando o modal está fechado).
 * Só a transição "precisava agir" → `WORKING` conta; carregar a tela já
 * conectado não confirma nada.
 */
export function hasReconnected(
  previousNeedsAction: boolean,
  connection: WhatsAppConnection | null,
): boolean {
  return previousNeedsAction && connection !== null && connection.status === 'WORKING';
}

/** "desde 14:32" (hoje) ou "desde 29/10, 14:32" — horário de Rondônia. */
export function formatSince(since: string | null, now: Date = new Date()): string | null {
  if (since === null) return null;
  const date = new Date(since);
  if (Number.isNaN(date.getTime())) return null;
  const tz = 'America/Porto_Velho';
  const day = (d: Date): string =>
    d.toLocaleDateString('pt-BR', { timeZone: tz, day: '2-digit', month: '2-digit' });
  const time = date.toLocaleTimeString('pt-BR', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
  });
  return day(date) === day(now) ? `desde ${time}` : `desde ${day(date)}, ${time}`;
}

// ---------------------------------------------------------------------------
// "Depois" — por aba e por queda
// ---------------------------------------------------------------------------

export function guardDismissKey(agentId: string, since: string | null): string {
  return `bdp:wa-guard-dismissed:${agentId}:${since ?? 'never'}`;
}

/** Falha para `false`: storage indisponível mostra o aviso de novo. */
export function hasDismissedGuard(agentId: string, since: string | null): boolean {
  try {
    return window.sessionStorage.getItem(guardDismissKey(agentId, since)) === '1';
  } catch {
    return false;
  }
}

export function markGuardDismissed(agentId: string, since: string | null): void {
  try {
    window.sessionStorage.setItem(guardDismissKey(agentId, since), '1');
  } catch {
    // Sem storage: o espelho em memória do controlador segura nesta página.
  }
}
